import { format, parseISO } from "date-fns"
import readingTime from "reading-time"
import classNames from "classnames"

// ---------------------------------------------------------

import NewsletterCard from "@components/card-rounded/card-newsletter"
import Person from "@components/person"
import PingPong from "@components/pingpong"
import RecommendedReading from "@components/recommended-reading"
import RenderBlocks from "@lib/renderBlocks"
import TagList from "@components/tag-list"

// ---------------------------------------------------------

import {
  authors_container,
  blurb_container,
  boilerplate,
  content,
  hero,
  modules,
  read_time_label,
  newsletter_container,
  drop_cap
} from "./styles.module.scss"

// ---------------------------------------------------------

const BlogTemplate = ({ data, parent, recommended }) => {
  let {
    title,
    featuredImage,
    excerpt,
    date,
    authorCollection,
    modulesCollection,
    customReadTime,
    tagCollection,
    hasDropCap
  } = data

  if (hasDropCap == null) {
    hasDropCap = true
  }

  // ---------------------------------------------------------

  /**
   * Function that parses through data for all text, paragraph, and list-items to calculate reading time
   */
  const getAllText = () => {
    let stringBuilder = ""

    const contentBlocks =
      modulesCollection?.items &&
      modulesCollection?.items
        .filter((module) => {
          return module?.__typename === "Grid"
        })
        .map((module) => module.modulesCollection?.items[0])
        .filter((item) => {
          return item?.__typename === "ContentBlock"
        })
        .map((module) => module.content?.json.content)
        .flat()

    if (contentBlocks) {
      for (var i = 0; i < contentBlocks.length; i++) {
        for (var idx in contentBlocks[i].content) {
          evaluateContent(contentBlocks[i].content[idx])
        }
      }
    }

    function evaluateContent(node) {
      if (node.nodeType === "text") {
        stringBuilder += node.value
      } else if (node.nodeType === "paragraph" || node.nodeType === "list-item") {
        for (var i = 0; i < node.content.length; i++) {
          evaluateContent(node.content[i])
        }
      }
    }

    return stringBuilder
  }

  const readTime = customReadTime ? `${customReadTime} min read` : readingTime(getAllText()).text

  const sortedTags = tagCollection?.tags?.map((tag) => tag.name)

  // ---------------------------------------------------------

  const authorBlocks = authorCollection?.items.map((author, idx) => {
    const hasMultipleAssets = author.linkedFrom?.entryCollection?.total > 1

    return (
      <Person
        key={idx}
        image={author?.image?.url}
        name={author?.name}
        description={author?.description}
        url={hasMultipleAssets ? `/people/${author?.slug}` : null}
      />
    )
  })

  // ---------------------------------------------------------

  const authorShareColumn = (
    <aside className={blurb_container}>
      {authorCollection && <section className={authors_container}>{authorBlocks}</section>}
      <section className={newsletter_container}>
        <NewsletterCard />
      </section>
    </aside>
  )

  // ---------------------------------------------------------

  const getAnnouncementBoilerplate = () => (
    <div className={boilerplate}>
      <h4>About Amperity</h4>
      <p>
        Amperity's Customer Data Cloud empowers brands to transform raw customer data into strategic
        business assets with unprecedented speed and accuracy. Through AI-powered identity
        resolution, customizable data models, and intelligent automation, Amperity helps
        technologists eliminate data bottlenecks and accelerate business impact. More than 400
        leading brands worldwide, including Alaska Airlines, DICK'S Sporting Goods, BECU, Virgin
        Atlantic, and Wyndham Hotels & Resorts, rely on Amperity to drive customer insights and
        revenue growth. Founded in 2016, Amperity operates globally with offices in Seattle, New
        York City, London, and Melbourne. For more information, visit{" "}
        <a href="https://amperity.com">amperity.com</a> or follow us on{" "}
        <a
          href="https://www.linkedin.com/company/amperity"
          target="_blank"
          rel="noopener noreferrer"
        >
          LinkedIn
        </a>
        ,{" "}
        <a href="https://twitter.com/amperity" target="_blank" rel="noopener noreferrer">
          X
        </a>
        ,{" "}
        <a href="https://www.facebook.com/goamperity" target="_blank" rel="noopener noreferrer">
          Facebook
        </a>{" "}
        and{" "}
        <a href="https://www.instagram.com/amperity/" target="_blank" rel="noopener noreferrer">
          Instagram
        </a>
        .
      </p>
    </div>
  )

  // ---------------------------------------------------------

  return (
    <>
      <PingPong
        imageOnLeft
        image={{
          alt: featuredImage?.description,
          height: featuredImage?.height,
          src: featuredImage?.url,
          width: featuredImage?.width
        }}
        backgroundColor="cement"
        className={hero}
      >
        <span className="label">
          {date && format(parseISO(date), "MMMM d, yyyy")} |{" "}
          <span className={read_time_label}>{readTime}</span>
        </span>
        <h1>{title}</h1>
        <p>{excerpt}</p>
        {tagCollection && <TagList tags={sortedTags} path="/blog" theme="light" />}
      </PingPong>

      <article className={content} data-cy="blog-content-container">
        {authorShareColumn}
        <div className={classNames(modules, hasDropCap ? drop_cap : null)}>
          {RenderBlocks(modulesCollection?.items)}
        </div>
        {parent === "announcements" && getAnnouncementBoilerplate()}
      </article>

      <RecommendedReading recommended={recommended} />
    </>
  )
}

export default BlogTemplate
